<template>
  <v-container fluid class="donations-container">
    <!-- Enhanced Header Section with Particles Background -->
    <div class="header-section" ref="headerSection">
      <div class="particles-bg"></div>
      <div class="header-content">
        <div class="title-wrapper" >
          <div class="icon-container">
            <v-icon class="header-icon" size="40" color="#fff">mdi-heart</v-icon>
            <div class="icon-pulse"></div>
          </div>
          <div>
            <h1 class="page-title">Donations</h1>
            <p class="page-subtitle">Empowering communities through generosity</p>
          </div>
        </div>
        <div class="header-stats" >
          <div class="stat-card" @click="animateStatCard($event)" v-ripple>
            <div class="stat-icon">
              <v-icon size="24" color="#fff">mdi-cash-multiple</v-icon>
            </div>
            <div class="stat-value" ref="totalAmount">$ {{ animatedTotalDonations  / 100 }}</div>
            <div class="stat-label">Total Raised</div>
          </div>
          <div class="stat-card" @click="animateStatCard($event)" v-ripple>
            <div class="stat-icon">
              <v-icon size="24" color="#fff">mdi-account-group</v-icon>
            </div>
            <div class="stat-value" ref="contributors">{{ animatedContributors }}</div>
            <div class="stat-label">Contributors</div>
          </div>
          <!-- <div class="stat-card" @click="animateStatCard($event)" v-ripple>
            <div class="stat-icon">
              <v-icon size="24" color="#fff">mdi-chart-line</v-icon>
            </div>
            <div class="stat-value">{{ growthPercentage }}%</div>
            <div class="stat-label">Growth</div>
          </div> -->
        </div>
      </div>
    </div>

    <!-- Enhanced Filter Section with Floating Action Button -->
    <div class="filter-section" >
      <v-card class="filter-card mb-6" elevation="0">
        <v-card-text class="pa-4">
          <v-row align="center" class="filter-row">
            <v-col cols="12" md="4">
              <div class="search-wrapper">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="mdi-magnify"
                  label="Search donations..."
                  variant="outlined"
                  density="compact"
                  hide-details
                  clearable
                  @input="onSearchInput"
                  class="search-field"
                />
                <div class="search-suggestions" v-if="searchSuggestions.length > 0 && search">
                  <div
                    v-for="suggestion in searchSuggestions"
                    :key="suggestion"
                    @click="search = suggestion"
                    class="suggestion-item"
                  >
                    {{ suggestion }}
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="3">
                <v-select
                  v-model="amountFilter"
                  :items="amountFilterOptions"
                  label="Filter by amount"
                  item-text="title"
                  item-value="value"
                  variant="outlined"
                  density="compact"
                  hide-details
                  @change="applyFilters"
                  class="filter-select"
                />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="sortBy"
                :items="sortOptions"
                label="Sort by"
                item-value="value"
                item-text="title"
                variant="outlined"
                density="compact"
                hide-details
                @update:modelValue="applySorting"
                class="filter-select"
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                color="primary"
                variant="flat"
                block
                prepend-icon="mdi-export"
                @click="exportToPDF"
                class="export-btn"
                :loading="exportLoading"
              >
                Export
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>

    <!-- Mobile Cards View with Enhanced Design -->
    <div class="d-flex d-md-none mobile-cards-container">
      <v-row>
        <v-col
          v-for="(donation, index) in paginatedDonations"
          :key="donation._id || index"
          cols="12"
          sm="6"
        >
          <v-card
            class="donation-mobile-card"
            elevation="0"
            @click="onSelectDonation(donation)"
            :class="{ 'card-selected': selectedDonations.includes(donation._id) }"
            v-ripple
          >
            <div class="card-glow"></div>
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-3">
                <div class="avatar-wrapper">
                  <v-avatar size="48" class="me-3">
                    <v-img
                      :src="donation.agency_id?.midThumbnail || placeholderImage"
                      :alt="donation.agency_id?.name || 'Agency'"
                    />
                  </v-avatar>
                  <div class="avatar-badge" v-if="donation.isVerified">
                    <v-icon size="12" color="white">mdi-check</v-icon>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <div class="donor-name">{{ donation.agency_id?.name || 'Unknown' }}</div>
                  <div class="donor-email">{{ donation.agency_id?.email || 'No email' }}</div>
                </div>
                <!-- <v-btn
                  icon
                  size="small"
                  variant="text"
                  @click.stop="toggleFavorite(donation)"
                  class="favorite-btn"
                >
                  <v-icon :color="donation.isFavorite ? 'red' : 'grey'">
                    {{ donation.isFavorite ? 'mdi-heart' : 'mdi-heart-outline' }}
                  </v-icon>
                </v-btn> -->
              </div>
              <v-divider class="mb-3" />
              <div class="d-flex justify-space-between align-center">
                <div>
                  <div class="donation-amount">{{ formatCurrency(donation.total_amount ?? 0) }}</div>
                  <div class="donation-date">{{ formatDate(donation.createdAt) }}</div>
                </div>
                <div class="action-buttons">
              <v-btn
                icon
                size="small"
                variant="text"
                @click.stop="payDonation(item)"
                class="action-btn"
              >
                <v-icon color="primary">mdi mdi-cash-sync</v-icon>
              </v-btn>
              <v-btn
                icon
                size="small"
                variant="text"
                @click.stop="viewHistory(item)"
                class="action-btn"
              >
                <v-icon color="success">mdi-history</v-icon>
              </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Enhanced Desktop Table View -->
    <div class="d-none d-md-block desktop-table-container" >
      <v-card class="table-card" elevation="0">
        <div class="table-header">
          <div class="table-title">
            <v-icon class="me-2">mdi-table</v-icon>
            Donations Overview
          </div>
          <div class="table-actions">
            <!-- <v-btn
              variant="outlined"
              size="small"
              @click="selectAll"
              class="me-2"
            >
              {{ selectedDonations.length > 0 ? 'Deselect All' : 'Select All' }}
            </v-btn> -->
            <v-btn
              variant="outlined"
              size="small"
              @click="refreshData"
              :loading="loading"
              class="me-2"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </div>
        </div>
        <v-data-table
          :headers="headers"
          :items="paginatedDonations"
          :items-per-page="itemsPerPage"
          :loading="loading"
          :search="search"
          @click:row="onSelectDonation"
          class="modern-table"
          hover
          v-model="selectedDonations"
          item-value="_id"
        >
          <template v-slot:[`item.agency_id`]="{ item }">
            <div class="donor-info">
              <div class="avatar-wrapper">
                <v-avatar size="40" class="me-3">
                  <v-img
                    :src="item.agency_id?.midThumbnail || placeholderImage"
                    :alt="item.agency_id?.name || 'Agency'"
                  />
                </v-avatar>
                <div class="avatar-badge" v-if="item.isVerified">
                  <v-icon size="10" color="white">mdi-check</v-icon>
                </div>
              </div>
              <div>
                <div class="donor-name">{{ item.agency_id?.name || 'Unknown' }}</div>
                <div class="donor-email">{{ item.agency_id?.email || 'No email' }}</div>
              </div>
            </div>
          </template>
          
          <template v-slot:[`item.total_amount`]="{ item }">
            <div class="price-wrapper">
              <span class="price-amount">{{ formatCurrency(item.total_amount  / 100 ?? 0) }}</span>
              <div class="price-trend" v-if="item.trend">
                <v-icon 
                  :color="item.trend > 0 ? 'success' : 'error'"
                  size="16"
                >
                  {{ item.trend > 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
                </v-icon>
              </div>
            </div>
          </template>

          <template v-slot:[`item.with_charges`]="{ item }">
            <div class="charges-wrapper">
              <span class="charges-amount">{{ formatCents(item.with_charges  ?? 0) }}</span>
            </div>
          </template>

          <template v-slot:[`item.balance`]="{ item }">
            <div class="balance-wrapper">
              <span class="balance-amount">{{ formatCents(item.balance ?? 0) }}</span>
            </div>
          </template>

          <template v-slot:[`item.paid_by_admin`]="{ item }">
            <span class="balance-amount">
              {{ formatCents(item.paid_by_admin)}}
            </span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="table-actions">
                <v-btn
                v-if="item.balance !== 0"
                icon
                size="small"
                variant="text"
                @click.stop="payDonation(item)"
                class="action-btn"
                >
                <v-icon color="primary">mdi mdi-cash-sync</v-icon>
                </v-btn>
              <v-btn
                icon
                size="small"
                variant="text"
                @click.stop="viewHistory(item)"
                class="action-btn"
              >
                <v-icon color="success">mdi-history</v-icon>
              </v-btn>
            </div>
          </template>

          <template v-slot:bottom>
            <div class="table-footer">
              <div class="footer-info">
                <span class="items-count">
                  {{ filteredDonations.length }} total items
                </span>
                <span class="selected-count" v-if="selectedDonations.length > 0">
                  {{ selectedDonations.length }} selected
                </span>
              </div>
              <v-pagination
                v-model="page"
                :length="Math.ceil(filteredDonations.length / itemsPerPage)"
                :total-visible="7"
                class="ma-2"
                @update:modelValue="onPageChange"
              />
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- Enhanced Mobile Pagination -->
    <div class="d-flex d-md-none mobile-pagination" v-if="filteredDonations.length > itemsPerPage">
      <v-pagination
        v-model="page"
        :length="Math.ceil(filteredDonations.length / itemsPerPage)"
        :total-visible="5"
        class="ma-2"
        @update:modelValue="onPageChange"
      />
    </div>

    <!-- Loading Overlay -->
    <v-overlay v-model="loading" class="loading-overlay">
      <div class="loading-content">
        <v-progress-circular
          indeterminate
          size="64"
          width="4"
          color="primary"
        />
        <div class="loading-text">Loading donations...</div>
      </div>
    </v-overlay>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="showSuccessSnackbar"
      color="success"
      location="top"
      timeout="3000"
    >
      <v-icon class="me-2">mdi-check-circle</v-icon>
      {{ successMessage }}
    </v-snackbar>

    <!-- Add Donation Dialog -->
    <admin-transfer-amounts
      :dialog="transferDialog"
      :onClose="onCloseDialog"
      :donation="respectiveAgencyDonation"
    />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AdminTransferAmounts from './AdminTransferAmounts.vue';
import jsPDF from "jspdf";
import "jspdf-autotable";

export default {
  name: 'DonationsManager',
  components: {
    AdminTransferAmounts
  },
  data() {
    return {
      loading: false,
      search: '',
      page: 1,
      itemsPerPage: 15,
      amountFilter: 'all',
      sortBy: 'date_desc',
      placeholderImage: require("@/assets/default.jpg"),
      selectedDonations: [],
      showFab: true,
      showAddDonationDialog: false,
      showSuccessSnackbar: false,
      successMessage: '',
      exportLoading: false,
      addingDonation: false,
      formValid: false,
      animatedTotalDonations: 0,
      animatedContributors: 0,
      transferDialog: false,
      searchSuggestions: [],
      respectiveAgencyDonation: null,
      newDonation: {
        agencyName: '',
        amount: '',
        email: ''
      },
      headers: [
        { text: "Agency", value: "agency_id", sortable: false, width: "25%" },
        { text: "Total Amount", value: "total_amount", sortable: true, width: "15%" },
        { text: "To Pay", value: "with_charges", sortable: true, width: "15%" },
        { text: "Balance", value: "balance", sortable: true, width: "15%" },
        { text: "Paid", value: "paid_by_admin", sortable: true, width: "15%" },
        { text: "Actions", value: "actions", sortable: false, width: "5%" }
      ],
      amountFilterOptions: [
        { title: 'All Amounts', value: 'all' },
        { title: 'Under $500', value: 'under500' },
        { title: '$500 - $1000', value: '500to1000' },
        { title: '$1000 - $5000', value: '1000to5000' },
        { title: 'Over $5000', value: 'over5000' }
      ],
      sortOptions: [
        { title: 'Date (Newest)', value: 'date_desc' },
        { title: 'Date (Oldest)', value: 'date_asc' },
        { title: 'Amount (Highest)', value: 'amount_desc' },
        { title: 'Amount (Lowest)', value: 'amount_asc' },
        { title: 'Agency Name (A-Z)', value: 'name_asc' },
        { title: 'Agency Name (Z-A)', value: 'name_desc' }
      ]
    };
  },

  computed: {
    ...mapGetters({
      allDonations: 'donation/getAllDonationsRecords'
    }),
    
donationsArray() {
  // Support both direct array and { res: [...] }
  const arr = Array.isArray(this.allDonations)
    ? this.allDonations
    : (Array.isArray(this.allDonations?.res) ? this.allDonations.res : []);
  return arr.filter(d => d && d.agency_id);
},
    
    filteredDonations() {
      let filtered = [...this.donationsArray];

      // Apply search filter
      if (this.search) {
        const searchTerm = this.search.toLowerCase();
        filtered = filtered.filter(donation => {
          const agencyName = donation.agency_id?.name?.toLowerCase() || '';
          const email = donation.agency_id?.email?.toLowerCase() || '';
          return agencyName.includes(searchTerm) || email.includes(searchTerm);
        });
      }

      // Apply amount filter
      if (this.amountFilter !== 'all') {
        filtered = filtered.filter(donation => {
          const amount = donation.total_amount || 0;
          switch (this.amountFilter) {
            case 'under500': return amount < 500;
            case '500to1000': return amount >= 500 && amount <= 1000;
            case '1000to5000': return amount >= 1000 && amount <= 5000;
            case 'over5000': return amount > 5000;
            default: return true;
          }
        });
      }

      // Apply sorting
      filtered.sort((a, b) => {
        switch (this.sortBy) {
          case 'date_desc':
            return new Date(b.createdAt) - new Date(a.createdAt);
          case 'date_asc':
            return new Date(a.createdAt) - new Date(b.createdAt);
          case 'amount_desc':
            return (b.total_amount || 0) - (a.total_amount || 0);
          case 'amount_asc':
            return (a.total_amount || 0) - (b.total_amount || 0);
          case 'name_asc':
            return (a.agency_id?.name || '').localeCompare(b.agency_id?.name || '');
          case 'name_desc':
            return (b.agency_id?.name || '').localeCompare(a.agency_id?.name || '');
          default:
            return 0;
        }
      });

      return filtered;
    },
    
    paginatedDonations() {
      const start = (this.page - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredDonations.slice(start, end);
    },
    
    totalDonations() {
      return this.filteredDonations.reduce((total, d) => total + (d.total_amount || 0), 0);
    },
    
    uniqueAgenciesCount() {
      const agencyIds = new Set();
      this.filteredDonations.forEach(donation => {
        if (donation.agency_id?._id) {
          agencyIds.add(donation.agency_id._id);
        }
      });
      return agencyIds.size;
    },
    
    growthPercentage() {
      // Mock growth calculation - replace with actual logic
      return Math.floor(Math.random() * 30) + 10;
    }
  },

  watch: {
    totalDonations: {
      handler(newVal) {
        this.animateNumber(this.animatedTotalDonations, newVal, 'animatedTotalDonations');
      },
      immediate: true
    },
    
    uniqueAgenciesCount: {
      handler(newVal) {
        this.animateNumber(this.animatedContributors, newVal, 'animatedContributors');
      },
      immediate: true
    },
      allDonations: {
  
    immediate: true,
    deep: true
  }
  },

  methods: {
    ...mapActions({
      fetchAllDonations: 'donation/adminDonationsHistory',
    }),

    // Animation Methods
    animateNumber(from, to, property) {
      const duration = 1000;
      const startTime = performance.now();
      
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        const currentValue = Math.floor(from + (to - from) * this.easeOutQuart(progress));
        this[property] = currentValue;
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
    },

    easeOutQuart(t) {
      return 1 - Math.pow(1 - t, 4);
    },

    animateStatCard(event) {
      const card = event.currentTarget;
      card.style.transform = 'scale(0.95)';
      setTimeout(() => {
        card.style.transform = 'scale(1)';
      }, 150);
    },

    // Search and Filter Methods
    onSearchInput() {
      this.updateSearchSuggestions();
      this.page = 1;
    },

    updateSearchSuggestions() {
      if (!this.search || this.search.length < 2) {
        this.searchSuggestions = [];
        return;
      }

      const suggestions = new Set();
      this.donationsArray.forEach(donation => {
        const name = donation.agency_id?.name || '';
        const email = donation.agency_id?.email || '';
        
        if (name.toLowerCase().includes(this.search.toLowerCase())) {
          suggestions.add(name);
        }
        if (email.toLowerCase().includes(this.search.toLowerCase())) {
          suggestions.add(email);
        }
      });

      this.searchSuggestions = Array.from(suggestions).slice(0, 5);
    },

    applyFilters() {
      this.page = 1;
    },

    applySorting() {
      this.page = 1;
    },

    // Selection Methods
    selectAll() {
      if (this.selectedDonations.length > 0) {
        this.selectedDonations = [];
      } else {
        this.selectedDonations = this.paginatedDonations.map(d => d._id);
      }
    },

    // Action Methods
    onSelectDonation(donation) {
      this.$router.push({ 
        name: 'agency-donation-details', 
        params: { id: donation.agency_id._id } 
      });
    },

    viewHistory(donation) {
      this.$router.push({ 
        name: 'agency-donation-details', 
        params: { id: donation.agency_id._id } 
      });
    },

    payDonation(donation) {
      // Implement edit functionality
      this.transferDialog = true;
      this.respectiveAgencyDonation = donation;
      console.log('Selected Donation for Transfer:', donation);
    },
    onCloseDialog() {
      this.transferDialog = false;
      this.fetchAllDonations()
    },
    toggleFavorite(donation) {
      // Toggle favorite status
      donation.isFavorite = !donation.isFavorite;
      this.showSuccessMessage(`Donation ${donation.isFavorite ? 'added to' : 'removed from'} favorites`);
    },

    addDonation() {
      if (!this.formValid) return;
      
      this.addingDonation = true;
      
      // Simulate API call
      setTimeout(() => {
        this.addingDonation = false;
        this.showAddDonationDialog = false;
        this.newDonation = { agencyName: '', amount: '', email: '' };
        this.showSuccessMessage('Donation added successfully!');
        this.refreshData();
      }, 1500);
    },

    // Utility Methods
    formatCurrency(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    },

    formatCents(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount / 100);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },

    showSuccessMessage(message) {
      this.successMessage = message;
      this.showSuccessSnackbar = true;
    },

    onPageChange() {
      // Smooth scroll to top when page changes
      this.$el.scrollIntoView({ behavior: 'smooth' });
    },

    refreshData() {
      this.loading = true;
      this.fetchAllDonations()
        .then(() => {
          this.showSuccessMessage('Data refreshed successfully!');
        })
        .finally(() => {
          this.loading = false;
        });
    },

    exportToPDF() {
      this.exportLoading = true;
      
      setTimeout(() => {
      const doc = new jsPDF();
      const title = 'Donations Report';
      const date = new Date().toLocaleDateString();

      // Add header
      doc.setFontSize(20);
      doc.setTextColor(8, 68, 104);
      doc.text(title, 14, 20);
      
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated on: ${date}`, 14, 30);
      doc.text(`Total Donations: ${this.formatCurrency(this.totalDonations)}`, 14, 38);
      doc.text(`Total Contributors: ${this.uniqueAgenciesCount}`, 14, 46);

      const headers = [['Agency', 'Total Amount', 'With Charges', 'Balance']];

      const data = this.filteredDonations.map(donation => [
        donation.agency_id?.name || 'Unknown',
        this.formatCurrency(donation.total_amount || 0),
        this.formatCurrency(donation.with_charges || 0),
        this.formatCurrency(donation.balance || 0),
        donation.paid_by_admin ? 'Paid' : 'Pending',
        this.formatDate(donation.createdAt)
      ]);

      doc.autoTable({
        head: headers,
        body: data,
        startY: 55,
        styles: {
        fontSize: 9,
        cellPadding: 3,
        valign: 'middle',
        halign: 'center' // Center align all cells
        },
        headStyles: {
        fillColor: [8, 68, 104],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center' // Center align header
        },
        alternateRowStyles: {
        fillColor: [245, 247, 250]
        },
        margin: { top: 55 }
      });

      doc.save(`donations-report-${date.replace(/\//g, '-')}.pdf`);
      this.exportLoading = false;
      this.showSuccessMessage('Report exported successfully!');
      }, 1000);
    },

    loadAllDonations() {
      this.loading = true;
      this.fetchAllDonations()
        .finally(() => { 
          this.loading = false; 
        });
    }
  },

  mounted() {
    this.loadAllDonations();
    
  
  }
};
</script>

<style scoped>
.donations-container {
  min-height: 100vh;
  padding: 24px;
  position: relative;
}

/* Enhanced Header Styles */
.header-section {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 50%, #2c5f7a 100%);
  border-radius: 24px;
  padding: 40px;
  margin-bottom: 32px;
  color: white;
  box-shadow: 0 20px 40px rgba(8, 68, 104, 0.3);
  position: relative;
  overflow: hidden;
}

.particles-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="80" cy="30" r="1.5" fill="rgba(255,255,255,0.15)"/><circle cx="40" cy="70" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="90" cy="80" r="1.2" fill="rgba(255,255,255,0.12)"/><circle cx="10" cy="90" r="0.8" fill="rgba(255,255,255,0.08)"/></svg>');
  animation: float 20s infinite linear;
}

@keyframes float {
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
  100% { transform: translateY(0px) rotate(360deg); }
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  position: relative;
  z-index: 1;
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 20px;
}

.icon-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 12px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.icon-pulse {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.1);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
}

.page-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin: 0;
  background: linear-gradient(45deg, #ffffff, #e3f2fd);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
}

.page-subtitle {
  font-size: 1.1rem;
  margin: 8px 0 0 0;
  opacity: 0.9;
  font-weight: 300;
}

.header-stats {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.stat-card {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 24px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  min-width: 140px;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.2);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.5s;
}

.stat-card:hover::before {
  left: 100%;
}

.stat-icon {
  margin-bottom: 8px;
  opacity: 0.8;
}

.stat-value {
  font-size: 2.2rem;
  font-weight: 800;
  margin-bottom: 8px;
  color: #ffffff;
}

.stat-label {
  font-size: 0.9rem;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

/* Enhanced Filter Section */
.filter-section {
  margin-bottom: 24px;
}

.filter-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.filter-row {
  align-items: center;
}

.search-wrapper {
  position: relative;
}

.search-field {
  transition: all 0.3s ease;
}

.search-field:focus-within {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.search-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
}

.suggestion-item {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s ease;
}

.suggestion-item:hover {
  background: #f8f9fa;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.filter-select {
  transition: all 0.3s ease;
}

.filter-select:focus-within {
  transform: translateY(-2px);
}

.export-btn {
  background: linear-gradient(135deg, #084468, #3b7ca1) !important;
  color: white !important;
  border-radius: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.export-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(8, 68, 104, 0.3);
}

/* Floating Action Button */
.floating-action-btn {
  position: fixed !important;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  background: linear-gradient(135deg, #084468, #3b7ca1) !important;
  box-shadow: 0 8px 25px rgba(8, 68, 104, 0.3);
  transition: all 0.3s ease;
}

.floating-action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 12px 30px rgba(8, 68, 104, 0.4);
}

/* Enhanced Mobile Cards */
.mobile-cards-container {
  margin-bottom: 24px;
}

.donation-mobile-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.donation-mobile-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.donation-mobile-card.card-selected {
  border-color: #084468;
  box-shadow: 0 0 0 2px rgba(8, 68, 104, 0.2);
}

.card-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(8, 68, 104, 0.05), rgba(59, 124, 161, 0.05));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.donation-mobile-card:hover .card-glow {
  opacity: 1;
}

.avatar-wrapper {
  position: relative;
  display: inline-block;
}

.avatar-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  background: #4CAF50;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
}

.donor-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 1.1rem;
  margin-bottom: 4px;
}

.donor-email {
  font-size: 0.9rem;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.favorite-btn {
  transition: all 0.3s ease;
}

.favorite-btn:hover {
  transform: scale(1.1);
}

.donation-amount {
  font-weight: 700;
  font-size: 1.3rem;
  color: #059669;
  margin-bottom: 4px;
}

.donation-date {
  font-size: 0.9rem;
  color: #64748b;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-btn {
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

/* Enhanced Desktop Table */
.desktop-table-container {
  margin-bottom: 24px;
}

.table-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.table-header {
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
}

.table-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
}

.table-actions {
  display: flex;
  gap: 12px;
}

.modern-table {
  background: transparent;
}

.modern-table :deep(.v-data-table__wrapper) {
  border-radius: 0;
}

.modern-table :deep(.v-data-table-header) {
  background: linear-gradient(135deg, #084468, #3b7ca1);
}

.modern-table :deep(.v-data-table-header th) {
  color: white !important;
  font-weight: 600;
  padding: 20px 16px;
  border: none;
  background: transparent !important;
}

.modern-table :deep(.v-data-table-header th .v-data-table-header__content) {
  color: white !important;
  font-weight: 600;
}

.modern-table :deep(tbody tr) {
  transition: all 0.3s ease;
  cursor: pointer;
}

.modern-table :deep(tbody tr:hover) {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.modern-table :deep(tbody tr td) {
  padding: 20px 16px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.donor-info {
  display: flex;
  align-items: center;
}

.price-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price-amount {
  font-weight: 700;
  font-size: 1.1rem;
  color: #059669;
}

.price-trend {
  display: flex;
  align-items: center;
}

.charges-wrapper,
.balance-wrapper {
  display: flex;
  align-items: center;
}

.charges-amount,
.balance-amount {
  font-weight: 600;
  color: #374151;
}

.status-chip {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.table-footer {
  padding: 20px 24px;
  background: rgba(248, 250, 252, 0.8);
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.footer-info {
  display: flex;
  gap: 16px;
  align-items: center;
}

.items-count,
.selected-count {
  font-size: 0.9rem;
  color: #64748b;
}

.selected-count {
  font-weight: 600;
  color: #084468;
}

/* Mobile Pagination */
.mobile-pagination {
  justify-content: center;
  margin-top: 24px;
}

/* Dialog Styles */
.add-donation-card {
  border-radius: 20px;
  overflow: hidden;
}

.dialog-title {
  background: linear-gradient(135deg, #084468, #3b7ca1);
  color: white;
  font-weight: 600;
  padding: 20px 24px;
}

/* Loading Overlay */
/* .loading-overlay {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
} */

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-text {
  font-size: 1.1rem;
  color: #64748b;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .donations-container {
    padding: 16px;
  }
  
  .header-section {
    padding: 32px 20px;
  }
  
  .header-content {
    text-align: center;
  }
  
  .page-title {
    font-size: 2.5rem;
  }
  
  .page-subtitle {
    font-size: 1rem;
  }
  
  .header-stats {
    width: 100%;
    justify-content: center;
  }
  
  .stat-card {
    flex: 1;
    min-width: 120px;
    padding: 20px 16px;
  }
  
  .stat-value {
    font-size: 1.8rem;
  }
  
  .floating-action-btn {
    bottom: 16px;
    right: 16px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 2rem;
  }
  
  .header-stats {
    flex-direction: column;
    gap: 16px;
  }
  
  .stat-card {
    max-width: none;
    width: 100%;
  }
  
  .filter-row {
    flex-direction: column;
  }
  
  .filter-row .v-col {
    width: 100%;
  }
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #084468, #3b7ca1);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #063a5a, #2d6287);
}

/* Ripple Effect */
.v-ripple__container {
  border-radius: inherit;
}

/* Animation Classes */
.fade-in {
  animation: fadeIn 0.6s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.slide-up {
  animation: slideUp 0.6s ease-out;
}

@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* Focus States */
.v-btn:focus-visible {
  box-shadow: 0 0 0 3px rgba(8, 68, 104, 0.3);
}

.v-text-field:focus-within {
  box-shadow: 0 0 0 2px rgba(8, 68, 104, 0.2);
}

/* High Contrast Mode Support */
@media (prefers-contrast: high) {
  .donation-mobile-card,
  .table-card,
  .filter-card {
    border: 2px solid #000;
  }
  
  .header-section {
    border: 2px solid #000;
  }
}

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  
  .particles-bg {
    animation: none;
  }
  
  .icon-pulse {
    animation: none;
  }
}
</style>